import type { PcoRecommendations } from '@pco/api/graphQLDefinitions/queries/recommendations/pcoRecommendations.types';
import { useAddProductToCart } from '@pco/api/mesh/cart/useAddProductToCart';
import { Box } from '@pco/atoms/Box';
import { Icon } from '@pco/atoms/Icon';
import { productViewToProductCardView } from '@pco/modules/CategoriesNavigation/utils/useCategoryNavigation.impl';
import { getDataAttrs } from '@pco/utils';
import { useVehicle } from '@pco/utils/vehicle';
import { useEffect, useMemo, useState } from 'react';
import { CartDrawerHeader } from './components/CartDrawerHeader';
import { SheetProductCard } from './components/SheetProductCard';
import type { CartDrawerListProps, Status } from './index.types';

const dataAttrs = getDataAttrs(['molecule', 'cart-product-list']);

export const CartDrawerList: React.FC<CartDrawerListProps> = ({
	actionCTA,
	addDone,
	addInprogress,
	addError,
	placeholderImage,
	recommendations,
}) => {
	const vehicle = useVehicle();
	const [productsList, setProductsList] = useState<
		{ sku: string | null; status: Status }[]
	>([]);

	const { addProductToCart, isError, isSuccess } = useAddProductToCart();

	const recommendationsProps = useMemo(() => {
		if (recommendations?.totalResults) {
			const result =
				recommendations.results
					?.filter((r) => (r?.totalProducts || 0) > 0)
					?.reduce((min, curr) =>
						(curr?.displayOrder || 100) < (min?.displayOrder || 100)
							? curr
							: min,
					) || recommendations.results?.[0];
			return {
				listTitle: result?.storefrontLabel || 'No recommendations available',
				cardDetails: result?.productsView || [],
			};
		}
		return {
			listTitle: 'No recommendations available',
			cardDetails: [],
		};
	}, [recommendations]);

	useEffect(() => {
		if (isSuccess || isError) {
			setProductsList((prev) =>
				prev.map((p) =>
					p.status === 'pending'
						? { ...p, status: isSuccess ? 'success' : 'error' }
						: p,
				),
			);
		}
	}, [isSuccess, isError]);

	const addItemHandler = (productView: PcoRecommendations.ProductView) => {
		addProductToCart({
			productView: productViewToProductCardView(
				productView,
				productView.__typename === 'SimpleProductView'
					? productView.price
					: productView.dealer?.price,
			),
			quantity: 1,
			openDrawer: false,
			vehicle,
		});
		if (productView.sku) {
			setProductsList([
				...productsList,
				{
					sku: productView.sku ?? '',
					status: 'pending',
				},
			]);
		}
	};

	// if (isFetching) return null;

	const ctaLabels = {
		successLabel: addDone,
		errorLabel: addError,
		pendingLabel: addInprogress,
		defaultLabel: actionCTA.label ?? '',
	};

	return (
		<Box flexDirection="column" gap="24px" {...dataAttrs('root')()}>
			<CartDrawerHeader title={recommendationsProps.listTitle} />
			<Box
				flexDirection="column"
				gap="16px"
				padding-bottom="20px"
				{...dataAttrs('body')()}
			>
				{!recommendationsProps.cardDetails && (
					<Icon fallbackIcon="bounceload" width="20px" />
				)}
				{recommendationsProps.cardDetails.map((productView) => (
					<SheetProductCard
						key={productView?.sku}
						productView={productView}
						status={
							productsList.find((p) => p.sku === productView?.sku)?.status ??
							null
						}
						actionCTA={actionCTA}
						addItemHandler={addItemHandler}
						labels={ctaLabels}
						placeholderImage={placeholderImage}
					/>
				))}
			</Box>
		</Box>
	);
};
